import * as d3 from "d3";
import { MdLabel, MdLabelOff } from "react-icons/md";
import { renderToStaticMarkup } from "react-dom/server";

function convertLabel(key) {
  const map = {
    wind: "Wind_Forecaste",
    solar: "Solar_Forecaste",
    load: "Load_Forecaste",
  };
  return map[key] || key;
}

export const renderChart = (data, container, labelKey, selectedDate) => {
  if (!Array.isArray(data) || data.length === 0) return;

  const filteredData = data.filter(d => {
    const dataDateStr = d.date?.slice(0, 10);
    return dataDateStr === selectedDate;
  });

  const sortedData = filteredData.slice().sort((a, b) => a.block_no - b.block_no);
  d3.select(container).selectAll("svg").remove();
  const oldMsg = container.querySelector(".no-data-msg");
  if (oldMsg) oldMsg.remove();

  container.style.position = "relative";

  if (filteredData.length === 0) {
    const message = document.createElement("div");
    message.className = "no-data-msg";
    message.textContent = "No data available";
    Object.assign(message.style, {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      fontSize: "1.2rem",
      color: "#888",
    });
    container.appendChild(message);
    return;
  }

  const margin = { top: 20, right: 30, bottom: 60, left: 50 };
  const containerWidth = container.clientWidth || 600;
  const containerHeight = container.clientHeight || 300;
  const width = containerWidth - margin.left - margin.right;
  const height = containerHeight - margin.top - margin.bottom;

  const svg = d3
    .select(container)
    .append("svg")
    .attr("width", containerWidth)
    .attr("height", containerHeight)
    .append("g")
    .attr("transform", `translate(${margin.left},${margin.top})`);

  const timeSlots = Array.from({ length: 96 }, (_, i) => {
    const h = String(Math.floor(i / 4)).padStart(2, "0");
    const m = String((i % 4) * 15).padStart(2, "0");
    return `${h}:${m}`;
  });

  const processed = sortedData
    .map(row => {
      const rawLabel = row.model_name;
      const label = convertLabel(rawLabel);
      const value = parseFloat(row.forecast);
      const block = +row.block_no;
      return {
        time: timeSlots[block - 1],
        label,
        value,
        model_name: row.model_name,
      };
    })
    .filter(d => d.value !== null && d.value > 0 && !isNaN(d.value))
    .filter(d => d.label !== "user_hybrid");

  if (processed.length === 0) return;

  const grouped = d3.groups(processed, d => d.label);

  const x = d3.scalePoint().domain(timeSlots).range([0, width]);
  const y = d3
    .scaleLinear()
    .domain([0, d3.max(processed, d => d.value)])
    .nice()
    .range([height, 0]);

  const line = d3
    .line()
    .x(d => x(d.time))
    .y(d => y(d.value));

  svg.append("g")
    .attr("transform", `translate(0,${height})`)
    .call(d3.axisBottom(x).tickValues(timeSlots.filter((_, i) => i % 8 === 0)))
    .selectAll("text")
    .style("font-size", "14px")
    .style("font-weight", "bold")
    .attr("transform", "rotate(-30)")
    .style("text-anchor", "end");

  svg.append("g")
    .attr("class", "y-axis")
    .call(d3.axisLeft(y))
    .selectAll("text")
    .style("font-size", "14px")
    .style("font-weight", "bold");

  const color = d3.scaleOrdinal(d3.schemeCategory10);
  const hiddenMapKey = `hiddenSeries_${labelKey}`;
  const hiddenSeriesMap = JSON.parse(localStorage.getItem(hiddenMapKey)) || {};

  grouped.forEach(([label, values]) => {
    const sortedValues = values.slice().sort((a, b) =>
      timeSlots.indexOf(a.time) - timeSlots.indexOf(b.time)
    );

    svg.append("path")
      .datum(sortedValues)
      .attr("class", `series-line-${label.replace(/\s+/g, "-")}`)
      .attr("fill", "none")
      .attr("stroke", color(label))
      .attr("stroke-width", 2)
      .attr("d", line)
      .style("display", hiddenSeriesMap[label] ? "none" : "block");
  });

  const oldLegendDiv = container.querySelector(".legend-container");
  if (oldLegendDiv) oldLegendDiv.remove();
  const existingBtn = container.querySelector(".legend-toggle-btn");
  if (existingBtn) existingBtn.remove();

  const legendDiv = document.createElement("div");
  legendDiv.className = "legend-container";
  Object.assign(legendDiv.style, {
    position: "absolute",
    top: "40px",
    right: "20px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
    padding: "10px",
    zIndex: 100,
    minWidth: "180px",
    maxHeight: "200px",
    overflowY: "auto",
    background: "#fff",
    display: "none"
  });

  const searchInput = document.createElement("input");
  searchInput.type = "text";
  searchInput.placeholder = "Search series...";
  Object.assign(searchInput.style, {
    width: "100%",
    padding: "5px",
    marginBottom: "8px",
    border: "1px solid #ccc",
    borderRadius: "4px"
  });
  legendDiv.appendChild(searchInput);

  grouped.forEach(([label]) => {
    const row = document.createElement("div");
    Object.assign(row.style, {
      display: "flex",
      alignItems: "center",
      marginBottom: "6px",
      cursor: "pointer"
    });

    const colorBox = document.createElement("div");
    Object.assign(colorBox.style, {
      width: "12px",
      height: "12px",
      marginRight: "8px",
      backgroundColor: color(label),
      border: "1px solid #333"
    });

    const labelText = document.createElement("span");
    labelText.textContent = label;
    labelText.style.fontSize = "12px";
    labelText.style.textDecoration = hiddenSeriesMap[label] ? "line-through" : "none";

    row.onclick = () => {
      const isHidden = hiddenSeriesMap[label];
      hiddenSeriesMap[label] = !isHidden;
      localStorage.setItem(hiddenMapKey, JSON.stringify(hiddenSeriesMap));

      const path = svg.select(`.series-line-${label.replace(/\s+/g, "-")}`);
      path.style("display", !isHidden ? "none" : "block");

      labelText.style.textDecoration = !isHidden ? "line-through" : "none";
    };

    row.appendChild(colorBox);
    row.appendChild(labelText);
    legendDiv.appendChild(row);
  });

  container.appendChild(legendDiv);

  searchInput.addEventListener("input", () => {
    const query = searchInput.value;
    Array.from(legendDiv.querySelectorAll("div")).forEach(row => {
      const text = row.textContent;
      row.style.display = text.includes(query) ? "flex" : "none";
    });
  });

  const toggleButton = document.createElement("button");
  toggleButton.className = "legend-toggle-btn";
  Object.assign(toggleButton.style, {
    position: "absolute",
    top: "5px",
    right: "20px",
    zIndex: "110",
    fontSize: "14px",
    padding: "4px 8px",
    cursor: "pointer",
    borderRadius: "4px"
  });

  let legendVisible = false;
const showIcon = renderToStaticMarkup(<MdLabel size={24} />);
const hideIcon = renderToStaticMarkup(<MdLabelOff size={24} />);

toggleButton.innerHTML = `${showIcon} <span style="margin-left: 6px;"></span>`;

toggleButton.onclick = () => {
  legendVisible = !legendVisible;
  legendDiv.style.display = legendVisible ? "block" : "none";
  toggleButton.innerHTML = legendVisible
    ? `${hideIcon} <span style="margin-left: 6px;"></span>`
    : `${showIcon} <span style="margin-left: 6px;"></span>`;
};

  container.appendChild(toggleButton);

  const tooltip = d3.select(container)
    .append("div")
    .style("position", "absolute")
    .style("background", "#fff")
    .style("border", "1px solid #ccc")
    .style("borderRadius", "6px")
    .style("padding", "8px")
    .style("pointer-events", "none")
    .style("font-size", "12px")
    .style("box-shadow", "0 4px 8px rgba(0,0,0,0.1)")
    .style("display", "none")
    .style("z-index", "120");

  // Overlay to capture mouse events for tooltip
  const overlay = svg.append("rect")
    .attr("width", width)
    .attr("height", height)
    .style("fill", "none")
    .style("pointer-events", "all");

  overlay.on("mousemove", event => {
    const [mouseX, mouseY] = d3.pointer(event, overlay.node());

    const distances = timeSlots.map(t => Math.abs(x(t) - mouseX));
    const minDist = Math.min(...distances);
    const closestIndex = distances.indexOf(minDist);
    const closestTime = timeSlots[closestIndex];

    const dataAtTime = processed.filter(d => d.time === closestTime && !hiddenSeriesMap[d.label]);

    if (dataAtTime.length === 0) {
      tooltip.style("display", "none");
      return;
    }

    const html = `<strong>Time: ${closestTime}</strong><br/>` + dataAtTime
      .map(d => `<span style="color:${color(d.label)}">${d.label}</span>: ${d.value.toFixed(2)}`)
      .join("<br/>");

    tooltip.html(html)
      .style("display", "block");

    const tooltipEl = tooltip.node();
    const containerRect = container.getBoundingClientRect();
    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;

    let left = mouseX + margin.left + 15;
    let top = mouseY + margin.top + 15;

    if (left + tooltipWidth > containerRect.width) {
      left = mouseX + margin.left - tooltipWidth - 15;
    }
    if (top + tooltipHeight > containerRect.height) {
      top = mouseY + margin.top - tooltipHeight - 15;
    }

    tooltip.style("left", `${left}px`).style("top", `${top}px`);
  });

  overlay.on("mouseout", () => {
    tooltip.style("display", "none");
  });

  const xGrid = d3.axisBottom(x)
    .tickValues(timeSlots.filter((_, i) => i % 8 === 0))
    .tickSize(-height)
    .tickFormat("")
    .tickSizeOuter(0);

  const yGrid = d3.axisLeft(y)
    .ticks(6)
    .tickSize(-width)
    .tickFormat("")
    .tickSizeOuter(0);

  svg.append("g")
    .attr("class", "grid grid-y")
    .call(yGrid)
    .selectAll("line")
    .attr("stroke", "#ccc")
    .attr("stroke-dasharray", "4 4");

  svg.append("g")
    .attr("class", "grid grid-x")
    .attr("transform", `translate(0,${height})`)
    .call(xGrid)
    .selectAll("line")
    .attr("stroke", "#ccc")
    .attr("stroke-dasharray", "4 4");
};
